import { useEffect, useRef, useState } from "react";
import { isTouch, prefersReducedMotion } from "@/lib/motion";
import styles from "./CustomCursor.module.css";

/**
 * Desktop-only trailing cursor: an instant dot plus a lerped ring that grows
 * over interactive elements. Skipped on touch / reduced-motion / SSR.
 */
export function CustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const ringRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isTouch() || prefersReducedMotion()) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;
    setEnabled(true);

    const ring = ringRef.current;
    const dot = dotRef.current;
    if (!ring || !dot) return;

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let ringX = mouseX;
    let ringY = mouseY;
    let raf = 0;
    let visible = false;

    const onMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.transform = `translate(${mouseX}px, ${mouseY}px)`;
      if (!visible) {
        visible = true;
        ring.classList.remove(styles.hidden);
        dot.classList.remove(styles.hidden);
      }
      const target = e.target as HTMLElement | null;
      const interactive = target?.closest("a, button, [data-cursor], input, textarea, select");
      ring.classList.toggle(styles.hover, Boolean(interactive));
    };
    const onLeave = () => {
      ring.classList.add(styles.hidden);
      dot.classList.add(styles.hidden);
      visible = false;
    };
    const tick = () => {
      ringX += (mouseX - ringX) * 0.18;
      ringY += (mouseY - ringY) * 0.18;
      ring.style.transform = `translate(${ringX}px, ${ringY}px)`;
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  if (!enabled) return null;
  return (
    <>
      <div ref={ringRef} className={`${styles.ring} ${styles.hidden}`} aria-hidden="true" />
      <div ref={dotRef} className={`${styles.dot} ${styles.hidden}`} aria-hidden="true" />
    </>
  );
}
